import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import { WorkspaceErrorElement } from '../components/errors/RouteErrorElement';
import { loadRouteChunk } from '../forge/utils/lazyRouteModule';
import { ArchonOwnerOnly } from './components/ArchonOwnerOnly';
import { ArchonLayout } from './components/layout/ArchonLayout';
import { ArchonAccessProvider } from './context/ArchonAccessContext';
import { ARCHON_ROOT, ARCHON_TASKS_COSTS_PATH } from './paths';

// Page routes load lazily (route.lazy) so each Archon page is its own chunk and
// the main bundle stays free of Archon page code. This is the highest-value
// group to split: recharts, @tanstack/react-table, react-day-picker/date-fns and
// cmdk are reached only from Archon pages (the kit showcase pulls in nearly the
// whole kit on its own), so keeping them eager put all of it in every user's
// first load. The `/archon` layout element stays eager: it is the shell that has
// to render while a page chunk is still in flight.
/**
 * Summary became Tasks & Costs by chain, the split All chains opens on. An old
 * link keeps the period, dates and metric it was saved with; only the chain is set.
 */
function SummaryRedirect() {
  const params = new URLSearchParams(useLocation().search);
  params.set('chain', 'all');
  return <Navigate to={`${ARCHON_TASKS_COSTS_PATH}?${params}`} replace />;
}

export const archonRoutes: RouteObject = {
  path: ARCHON_ROOT,
  element: (
    <ArchonAccessProvider>
      <ArchonLayout />
    </ArchonAccessProvider>
  ),
  children: [
    {
      // Pathless, so it adds no segment: it exists only to own `errorElement`.
      // Sitting *inside* the layout route means a page crash replaces the
      // content area while the Archon sidebar stays mounted and navigable.
      errorElement: (
        <WorkspaceErrorElement
          homePath={ARCHON_TASKS_COSTS_PATH}
          homeLabel="Back to Tasks & Costs"
        />
      ),
      children: [
        { index: true, element: <Navigate to="tasks-costs" replace /> },
        { path: 'summary', element: <SummaryRedirect /> },
        {
          path: 'tasks-costs',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/TasksCostsPage')))
              .TasksCostsPage,
          }),
        },
        {
          path: 'tasks-costs/how-it-works',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/HowTheEstimateWorksPage')))
              .HowTheEstimateWorksPage,
          }),
        },
        {
          path: 'accuracy',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/AccuracyPage'))).AccuracyPage,
          }),
        },
        {
          path: 'accuracy/results/:qaTaskId',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/QaResultPage'))).QaResultPage,
          }),
        },
        {
          path: 'automatch-qa',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/AutomatchQaPage')))
              .AutomatchQaPage,
          }),
        },
        {
          path: 'labelers',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/LabelersPage'))).LabelersPage,
          }),
        },
        {
          path: 'labelers/gold/:taskId',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/GoldTaskPage'))).GoldTaskPage,
          }),
        },
        {
          path: 'labelers/quizzes/:quizId',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/QuizPage'))).QuizPage,
          }),
        },
        {
          path: 'per-store',
          handle: { pageWidth: 'fit' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/PerStorePage'))).PerStorePage,
          }),
        },
        {
          path: 'repeat-tasks',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/RepeatTasksPage')))
              .RepeatTasksPage,
          }),
        },
        {
          path: 'overlaps',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/OverlapsPage'))).OverlapsPage,
          }),
        },
        {
          path: 'match-trace',
          handle: { pageWidth: 'full' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/MatchTracePage')))
              .MatchTracePage,
          }),
        },
        {
          path: 'data/rollout-schedules',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/data/RolloutSchedulesPage')))
              .RolloutSchedulesPage,
          }),
        },
        {
          path: 'data/invoices',
          handle: { pageWidth: 'capped' },
          lazy: async () => {
            const { InvoicesPage } = await loadRouteChunk(
              () => import('./pages/data/InvoicesPage')
            );
            return {
              Component: () => (
                <ArchonOwnerOnly>
                  <InvoicesPage />
                </ArchonOwnerOnly>
              ),
            };
          },
        },
        {
          path: 'data/cost-model',
          handle: { pageWidth: 'capped' },
          lazy: async () => {
            const { CostModelPage } = await loadRouteChunk(
              () => import('./pages/data/CostModelPage')
            );
            return {
              Component: () => (
                <ArchonOwnerOnly>
                  <CostModelPage />
                </ArchonOwnerOnly>
              ),
            };
          },
        },
        {
          path: 'data/ingress-sources',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/data/IngressSourcesPage')))
              .IngressSourcesPage,
          }),
        },
        {
          path: 'data/qa-sampling',
          handle: { pageWidth: 'capped' },
          lazy: async () => {
            const { QaSamplingPage } = await loadRouteChunk(
              () => import('./pages/data/QaSamplingPage')
            );
            return {
              Component: () => (
                <ArchonOwnerOnly>
                  <QaSamplingPage />
                </ArchonOwnerOnly>
              ),
            };
          },
        },
        {
          path: 'data/match-config',
          handle: { pageWidth: 'capped' },
          lazy: async () => {
            const { MatchConfigPage } = await loadRouteChunk(
              () => import('./pages/data/MatchConfigPage')
            );
            return {
              Component: () => (
                <ArchonOwnerOnly>
                  <MatchConfigPage />
                </ArchonOwnerOnly>
              ),
            };
          },
        },
        {
          path: 'kit',
          handle: { pageWidth: 'capped' },
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/KitPage'))).KitPage,
          }),
        },
      ],
    },
  ],
};
